import { useTheme } from "@emotion/react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { tokens } from "../theme";
import FileService from "../services/doctorServices/FileService";

export default function CreateNewFile({
  openDialog,
  setOpenDialog,
  handleClose,
  onCreated,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('filesPage');
  const fileService = useMemo(() => new FileService(), []);

  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetState = () => {
    setName("");
    setError("");
    setIsSubmitting(false);
  };

  const closeDialog = () => {
    resetState();
    if (setOpenDialog) {
      setOpenDialog(false);
    }
    if (handleClose) {
      handleClose();
    }
  };

  const capitalizeWords = (str) => {
    if (!str) return str;
    // Önce tüm string'i küçük harfe çevir, sonra kelime başlarını büyük yap
    return str.trim().toLowerCase().replace(/(^|\s)\S/g, (match) => match.toUpperCase());
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t('pleaseEnterFileNameWithDot'));
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const capitalizedName = capitalizeWords(name);
      const data = { name: capitalizedName };
      const response = await fileService.createFile(data);
      console.log("File created successfully", response);
      if (onCreated) {
        onCreated(response);
      }
      closeDialog();
    } catch (err) {
      console.log("An error occurred while creating the file.", err);
      
      // 403 (Permission) errors are handled by global interceptor
      if (err.response?.status === 403) {
        closeDialog(); // Just close dialog, global alert will be shown
        return;
      }
      
      setError(t('errorCreatingFile'));
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={openDialog}
      onClose={closeDialog}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 60%, ${colors.blueAccent[900]}80 100%)`,
          borderRadius: 4,
          border: `1px solid ${colors.blueAccent[700]}40`,
          boxShadow: `0 18px 48px ${colors.primary[900]}55`,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 3, md: 4 },
          py: 3,
          background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 52,
            height: 52,
            backgroundColor: colors.primary[500],
            color: colors.grey[100],
            boxShadow: `0 10px 24px ${colors.primary[900]}55`,
          }}
        >
          <FolderOutlinedIcon sx={{ fontSize: 28 }} />
        </Avatar>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.4,
              color: colors.grey[100],
            }}
          >
            {t('createNewFileTitle')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.grey[200], opacity: 0.85 }}
          >
            {t('createNewFileSubtitle')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 3, md: 4 },
          py: 4,
          backgroundColor: `${colors.primary[600]}dd`,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              color: colors.grey[300],
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            {t('fileNameLabel')}
          </Typography>
          <TextField
            fullWidth
            placeholder={t('enterFileName')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) {
                setError("");
              }
            }}
            autoFocus
            error={!!error}
            helperText={error || t('fileNameHelperText')}
            FormHelperTextProps={{
              sx: {
                mt: 1,
                color: error ? colors.redAccent[300] : colors.grey[400],
              },
            }}
            InputProps={{
              sx: {
                borderRadius: 3,
                backgroundColor: colors.primary[500],
                color: colors.grey[100],
                "& fieldset": {
                  borderColor: error
                    ? `${colors.redAccent[400]}66`
                    : `${colors.primary[200]}44`,
                },
                "&:hover fieldset": {
                  borderColor: error
                    ? colors.redAccent[400]
                    : colors.greenAccent[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: error
                    ? colors.redAccent[500]
                    : colors.greenAccent[500],
                  boxShadow: error
                    ? `0 0 0 3px ${colors.redAccent[500]}33`
                    : `0 0 0 3px ${colors.greenAccent[500]}33`,
                },
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 3, md: 4 },
          py: 3,
          backgroundColor: `${colors.primary[600]}dd`,
          borderTop: `1px solid ${colors.primary[200]}33`,
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Button
          onClick={closeDialog}
          variant="outlined"
          disabled={isSubmitting}
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1.2,
            color: colors.grey[200],
            borderColor: colors.grey[500],
            fontWeight: 600,
            textTransform: "none",
            letterSpacing: 0.3,
            "&:hover": {
              borderColor: colors.grey[400],
              backgroundColor: `${colors.primary[500]}55`,
              transform: "translateY(-2px)",
            },
            transition: "all 0.25s ease",
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1.2,
            fontWeight: 700,
            textTransform: "none",
            letterSpacing: 0.4,
            background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
            color: colors.grey[100],
            boxShadow: `0 12px 28px ${colors.greenAccent[700]}45`,
            "&:hover": {
              transform: "translateY(-3px) scale(1.02)",
              boxShadow: `0 18px 36px ${colors.greenAccent[700]}60`,
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
            },
            "&.Mui-disabled": {
              backgroundColor: `${colors.greenAccent[500]}55`,
              color: `${colors.grey[300]}90`,
            },
            transition: "all 0.25s ease",
          }}
        >
          {isSubmitting ? t('creating') : t('createFile')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
